import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Loader2, FileText, CheckCircle } from 'lucide-react'
import { companyAPI } from '../../services/api'
import { toast } from '../../hooks/use-toast'

export default function PresentationModal({ open, onClose }) {
  const [loading, setLoading] = useState(false)
  const [fetchingData, setFetchingData] = useState(false)
  const [existingPresentation, setExistingPresentation] = useState(null)
  const [presentationData, setPresentationData] = useState({
    presentationFile: null,
    backupFile: null,
    notes: ''
  })

  // Fetch existing presentation when modal opens
  useEffect(() => {
    if (open) {
      fetchExistingPresentation()
    }
  }, [open])

  const fetchExistingPresentation = async () => {
    setFetchingData(true)
    try {
      const response = await companyAPI.getPresentation()
      if (response.data.success && response.data.presentation) {
        const presentation = response.data.presentation
        setExistingPresentation(presentation)

        // Pre-populate notes if they exist
        setPresentationData(prev => ({
          ...prev,
          notes: presentation.notes || ''
        }))
      }
    } catch (error) {
      // It's ok if no presentation exists yet
      console.log('No existing presentation found')
    } finally {
      setFetchingData(false)
    }
  }

  const handleSubmit = async () => {
    try {
      setLoading(true)
      const formData = new FormData()

      if (presentationData.presentationFile) {
        formData.append('presentation', presentationData.presentationFile)
      }
      if (presentationData.backupFile) {
        formData.append('backup', presentationData.backupFile)
      }
      formData.append('notes', presentationData.notes)

      // Use update endpoint if presentation exists, otherwise create
      const response = existingPresentation
        ? await companyAPI.updatePresentation(formData)
        : await companyAPI.uploadPresentation(formData)

      if (response.data) {
        toast.success(
          existingPresentation
            ? 'Presentation updated successfully!'
            : 'Presentation uploaded successfully!'
        )
        setPresentationData({
          presentationFile: null,
          backupFile: null,
          notes: ''
        })
        setExistingPresentation(null)
        onClose()
      }
    } catch (error) {
      console.error('Error uploading presentation:', error)
      toast.error('Failed to save presentation. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-auto">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl">{existingPresentation ? 'Update Presentation' : 'Upload Presentation'}</DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            {existingPresentation
              ? 'Replace or update your demo presentation and related materials'
              : 'Submit your demo presentation and related materials'
            }
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          {fetchingData ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2">Loading existing presentation...</span>
            </div>
          ) : (
          <>
          {existingPresentation && (
            <div className="bg-muted/50 p-4 rounded-lg mb-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-medium">Current Presentation</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span>{existingPresentation.presentationFileName}</span>
                  </div>
                  {existingPresentation.presentationPath && (
                    <a
                      href={existingPresentation.presentationPath}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline"
                    >
                      View/Download
                    </a>
                  )}
                </div>
                {existingPresentation.backupFileName && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span>Backup: {existingPresentation.backupFileName}</span>
                    </div>
                    {existingPresentation.backupPath && (
                      <a
                        href={existingPresentation.backupPath}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline"
                      >
                        View/Download
                      </a>
                    )}
                  </div>
                )}
                <div className="text-xs text-muted-foreground mt-2">
                  Uploaded on {new Date(existingPresentation.uploadedAt).toLocaleDateString()}
                </div>
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="presentation">
              Presentation File
              {existingPresentation && <span className="text-xs text-muted-foreground ml-2">(Leave empty to keep existing)</span>}
            </Label>
            <Input
              id="presentation"
              type="file"
              accept=".ppt,.pptx,.pdf"
              onChange={(e) => setPresentationData({ ...presentationData, presentationFile: e.target.files[0] })}
            />
            <p className="text-xs text-muted-foreground mt-1">
              PowerPoint or PDF format (max 50MB)
            </p>
          </div>
          <div>
            <Label htmlFor="backup">Backup File (Optional)</Label>
            <Input
              id="backup"
              type="file"
              accept=".ppt,.pptx,.pdf"
              onChange={(e) => setPresentationData({ ...presentationData, backupFile: e.target.files[0] })}
            />
          </div>
          <div>
            <Label htmlFor="notes">Presentation Notes</Label>
            <Textarea
              id="notes"
              placeholder="Any special requirements or notes..."
              rows={3}
              value={presentationData.notes}
              onChange={(e) => setPresentationData({ ...presentationData, notes: e.target.value })}
            />
          </div>
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || (!presentationData.presentationFile && !existingPresentation)}
              className="w-full sm:w-auto"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                existingPresentation ? 'Update Files' : 'Upload Files'
              )}
            </Button>
          </div>
          </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}